import type {
  AccessGrant,
  AccessRole,
  BulkAccessMode,
  ClauseBulkAccessRequest,
  ClauseBulkAccessResult,
  ClauseBulkApplyTo,
} from '~/features/access-control/types/AccessControl';

import { filterTargetsByApplyTo } from '~/features/access-control/utils/bulkAccess';

export type ServerClauseBulkTargetType = ClauseBulkApplyTo;

export type ServerClauseBulkOperationType = 'append' | 'replace' | 'remove';

const OPERATION_TYPE_BY_MODE = {
  add: 'append',
  override: 'replace',
} as const satisfies Record<BulkAccessMode, ServerClauseBulkOperationType>;

type ServerClauseBulkPrincipalGrant = {
  id: string;
  level: AccessRole;
};

export type ServerClauseBulkAccessRequest = {
  target_type: ServerClauseBulkTargetType;
  operation_type: ServerClauseBulkOperationType;
  clause_ids: string[];
  variation_ids: string[];
  everyone: AccessRole | null;
  users: ServerClauseBulkPrincipalGrant[];
  departments: ServerClauseBulkPrincipalGrant[];
};

export type ServerClauseBulkAccessResult = {
  job_id: string;
};

function partitionGrants(grants: AccessGrant[]) {
  let everyone: AccessRole | null = null;
  const users: ServerClauseBulkPrincipalGrant[] = [];
  const departments: ServerClauseBulkPrincipalGrant[] = [];

  for (const grant of grants) {
    if (grant.principal.type === 'everyone') {
      everyone = grant.role;
      continue;
    }
    if (grant.principal.type === 'user') {
      users.push({ id: grant.principal.id, level: grant.role });
      continue;
    }
    departments.push({ id: grant.principal.id, level: grant.role });
  }

  return { departments, everyone, users };
}

export function mapClauseBulkAccessRequest(
  request: ClauseBulkAccessRequest,
): ServerClauseBulkAccessRequest {
  const scopedTargets = filterTargetsByApplyTo(
    request.targets,
    request.applyTo,
  );
  const clauseIds: string[] = [];
  const variationIds: string[] = [];

  for (const target of scopedTargets) {
    if (target.type === 'clause') {
      clauseIds.push(target.id);
    } else {
      variationIds.push(target.id);
    }
  }

  return {
    target_type: request.applyTo,
    operation_type: OPERATION_TYPE_BY_MODE[request.mode],
    clause_ids: clauseIds,
    variation_ids: variationIds,
    ...partitionGrants(request.grants),
  };
}

export function mapClauseBulkAccessResultResponse(
  serverResponse: ServerClauseBulkAccessResult,
  request: ClauseBulkAccessRequest,
): ClauseBulkAccessResult {
  const scopedTargets = filterTargetsByApplyTo(
    request.targets,
    request.applyTo,
  );

  return {
    mode: request.mode,
    applyTo: request.applyTo,
    updatedCount: scopedTargets.length,
    items: scopedTargets.map((target) => ({
      id: target.id,
      type: target.type,
      name: target.name,
    })),
    grantsApplied: request.grants,
    jobId: serverResponse.job_id,
  };
}
